import { useState } from "react";
import type { Exercise, AttemptResult } from "../../types";
import { api } from "../../api/client";
import FeedbackPanel from "./FeedbackPanel";

interface Props {
  exercise: Exercise;
  onDone: (passed: boolean) => void;
  onSkip?: () => void;
}

export default function FixSentenceExercise({ exercise, onDone, onSkip }: Props) {
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      setResult(await api.attemptExercise(exercise.id, answer));
    } catch (err) {
      console.error("Attempt failed:", err);
      setError("Couldn't check the answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setResult(null);
    setAnswer("");
    setError(null);
  };

  return (
    <div>
      <p className="text-[0.7rem] uppercase tracking-[0.12em] text-text-tertiary font-semibold mb-1.5">
        Rewrite correctly
      </p>
      <p className="text-[1.05rem] leading-relaxed font-display text-error/80 line-through decoration-error/40 mb-4">
        {exercise.text}
      </p>

      {!result && (
        <>
          <textarea
            className="input-base w-full resize-none focus:outline-none"
            rows={2}
            value={answer}
            autoFocus
            placeholder="Type the corrected sentence…"
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          />
          <div className="flex items-center gap-2 mt-3">
            <button className="btn-primary text-sm px-5" onClick={submit} disabled={submitting || !answer.trim()}>
              {submitting ? "Checking…" : "Check"} <span className="opacity-60 ml-1">⌘↵</span>
            </button>
            {onSkip && <button onClick={onSkip} className="btn-quiet text-xs px-3 py-1.5">Skip</button>}
          </div>
        </>
      )}

      {error && <p className="text-error text-xs mt-3">{error}</p>}

      {result && (
        <FeedbackPanel correct={result.is_correct} onNext={() => onDone(result.is_correct)} onRetry={retry}>
          {!result.is_correct && (
            <div>
              Your answer: <span className="text-error font-medium">{answer}</span>
            </div>
          )}
          <div>
            Suggested: <span className="text-success font-medium">{result.correct_answer}</span>
          </div>
          {result.feedback && <div className="italic">{result.feedback}</div>}
          {!result.feedback && result.explanation && <div className="italic">{result.explanation}</div>}
        </FeedbackPanel>
      )}
    </div>
  );
}
